import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/i18n";

const Line = ({ children, delay }) => (
  <span className="masked-line">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <motion.div style={{ y: yBlob }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <motion.div
          className="absolute top-1/4 -left-32 w-[34rem] h-[34rem] rounded-full bg-neon-magenta blur-[140px] opacity-20"
          animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-[30rem] h-[30rem] rounded-full bg-neon-cyan blur-[140px] opacity-15"
          animate={{ x: [0, -50, 0], y: [0, 50, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/3 left-1/2 w-[26rem] h-[26rem] rounded-full bg-gold blur-[160px] opacity-10"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-28">
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.7, rotate: -5, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, rotate: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.4, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.img
              src="/logo.png"
              alt="New Creation Studio"
              data-testid="hero-logo"
              animate={{
                y: [0, -14, 0],
                filter: [
                  "drop-shadow(0 0 0px rgba(212,175,55,0))",
                  "drop-shadow(0 0 30px rgba(212,175,55,0.35))",
                  "drop-shadow(0 0 0px rgba(212,175,55,0))",
                ],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.8 }}
              className="w-64 sm:w-80 lg:w-[26rem] h-auto"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="mt-10 text-xs sm:text-sm font-mono uppercase tracking-[0.35em] text-white/50"
            data-testid="hero-baseline"
          >
            {t("hero.baseline")}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-6 max-w-md text-base leading-relaxed text-white/70"
            data-testid="hero-subtitle"
          >
            {t("hero.subtitle")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap justify-center gap-4"
          >
            <a
              href="/prestations"
              data-testid="hero-cta-book"
              className="bg-gold text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light"
            >
              {t("hero.cta1")}
            </a>
            <a
              href="/contact"
              data-testid="hero-cta-gallery"
              className="border border-white/25 text-white px-8 py-4 text-sm uppercase tracking-[0.15em] transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              {t("hero.cta2")}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40"
        aria-hidden="true"
      >
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};
